import clsx from "clsx";

import type { Duration } from "date-fns";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import type { Option as TSelectorOption } from "@proda-ai/ui-kit";
import {
    Dropdown,
    TextField,
    DropdownContent,
    DropdownSelect,
    DropdownSelectItem,
} from "@proda-ai/ui-kit";
import React from "react";
import { parseDateIfItsValidAndMatchesFormat } from "./parseDateIfItsValidAndMatchesFormat";

export type TMonthsBasedDatePickerDataDuration = {
    type: "duration";
    value: Duration;
};
export type TMonthsBasedDatePickerDataManuallyTypedDate = {
    type: "date-manually-typed";
    value: string;
};
export type TMonthsBasedDatePickerDataDate = {
    type: "date";
    value: string;
};
export type TMonthsBasedDatePickerDataRange = {
    type: "range";
    start: string;
    end: string;
};
export type TMonthsBasedDatePickerData =
    | TMonthsBasedDatePickerDataDuration
    | TMonthsBasedDatePickerDataDate
    | TMonthsBasedDatePickerDataRange;

export type TMonthsBasedDatePickerItem<T = TMonthsBasedDatePickerData> = {
    data: T;
    label: string;
    id: string;
};
export type TMonthsBasedDatePickerOption<T = TMonthsBasedDatePickerData> =
    TSelectorOption<TMonthsBasedDatePickerItem<T>>;

export const isDataDurationTypeGuard = (
    data:
        | TMonthsBasedDatePickerData
        | TMonthsBasedDatePickerDataManuallyTypedDate,
): data is TMonthsBasedDatePickerDataDuration => {
    return data.type === "duration";
};
export const isDataDateTypeGuard = (
    data:
        | TMonthsBasedDatePickerData
        | TMonthsBasedDatePickerDataManuallyTypedDate,
): data is TMonthsBasedDatePickerDataDate => {
    return data.type === "date";
};
export const isDataRangeTypeGuard = (
    data:
        | TMonthsBasedDatePickerData
        | TMonthsBasedDatePickerDataManuallyTypedDate,
): data is TMonthsBasedDatePickerDataRange => {
    return data.type === "range";
};
export const isDataManuallyTypedTypeGuard = (
    data:
        | TMonthsBasedDatePickerData
        | TMonthsBasedDatePickerDataManuallyTypedDate,
): data is TMonthsBasedDatePickerDataDate => {
    return data.type === "date-manually-typed";
};

export const getLabelFromValue = (
    value: TMonthsBasedDatePickerItem<
        TMonthsBasedDatePickerData | TMonthsBasedDatePickerDataManuallyTypedDate
    >,
): string => {
    if (isDataRangeTypeGuard(value.data)) {
        // @FIXME this assumes that the picker is always a end date range picker
        return value.data.end;
    }
    return value.label;
};

const defaultFormat = "MM/yyyy";

export type MonthsBasedDatePickerProps<
    T extends TMonthsBasedDatePickerData = TMonthsBasedDatePickerData,
> = {
    value: TMonthsBasedDatePickerItem<
        T | TMonthsBasedDatePickerDataManuallyTypedDate
    >;
    label?: string;
    formatString?: string;
    disabled?: boolean;
    options: TMonthsBasedDatePickerOption<T>[];
    onChange: (
        value: TMonthsBasedDatePickerItem<
            T | TMonthsBasedDatePickerDataManuallyTypedDate
        >,
    ) => void;
    errorCallback?: (message: string) => void;
} & Pick<BoxProps, "sx" | "className">;

const testId = "MonthsBasedDatePicker";
const MonthsBasedDatePicker = <T extends TMonthsBasedDatePickerData>(
    props: MonthsBasedDatePickerProps<T>,
) => {
    const {
        sx,
        className,
        value,
        options,
        label = "",
        disabled = false,
        formatString = defaultFormat,
        onChange,
        errorCallback,
        ...otherProps
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);

    const [inputCurrentValue, setInputCurrentValue] = React.useState<string>(
        getLabelFromValue(value),
    );
    const resetInputCurrentValueToValueFromProps = React.useCallback(() => {
        setInputCurrentValue(getLabelFromValue(value));
    }, [value]);

    React.useEffect(() => {
        resetInputCurrentValueToValueFromProps();
    }, [resetInputCurrentValueToValueFromProps]);

    const onInvalidDate = React.useCallback(
        (message: string) => {
            resetInputCurrentValueToValueFromProps();
            errorCallback?.(message);
        },
        [errorCallback, resetInputCurrentValueToValueFromProps],
    );

    const onOptionManuallyTyped = React.useCallback(
        (typedString: string): void => {
            setIsDropdownOpen(false);

            const parsedDateOrFalse = parseDateIfItsValidAndMatchesFormat({
                dateString: typedString,
                formatString: formatString,
            });
            if (parsedDateOrFalse === false) {
                onInvalidDate?.("Invalid date format");
                return;
            }
            onChange({
                data: {
                    type: "date-manually-typed",
                    value: typedString,
                },
                label: typedString,
                id: `date-manually-typed--${typedString}`,
            });
        },
        [formatString, onChange, onInvalidDate],
    );

    const onOptionSelectedFromDropdown = React.useCallback(
        (selectedItem: TMonthsBasedDatePickerItem<T>) => {
            setIsDropdownOpen(false);
            onChange(selectedItem);
        },
        [onChange],
    );

    const handleSelect = React.useCallback(
        (focusedIndex?: number): void => {
            if (focusedIndex === undefined) return;

            const selectedOption = options[focusedIndex];
            if (!selectedOption) return;

            const selectedItem = selectedOption.item;
            onOptionSelectedFromDropdown(selectedItem);
        },
        [options, onOptionSelectedFromDropdown],
    );

    const openDropdownIfThereAreOptions = React.useCallback((): void => {
        if (options.length <= 0) return;

        setIsDropdownOpen(true);
    }, [options]);

    const currentIndex = React.useMemo(() => {
        return options.findIndex(
            option => option.item.label === inputCurrentValue,
        );
    }, [inputCurrentValue, options]);

    return (
        <Box
            data-testid={testId}
            className={clsx([testId, className])}
            sx={{
                ...sx,
            }}
            {...otherProps}
        >
            <Dropdown
                isShowing={isDropdownOpen}
                onClose={(): void => setIsDropdownOpen(false)}
                isDismissible={true}
                onItemSelected={handleSelect}
            >
                <TextField
                    ref={inputRef}
                    id={testId}
                    name={testId}
                    label={label}
                    size={"tiny"}
                    disabled={disabled}
                    leadingIcon={{
                        icon: "calendar-days",
                        color: "accent-color",
                    }}
                    trailingIcon="caret-down"
                    value={inputCurrentValue}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setInputCurrentValue(event.target.value);
                    }}
                    onFocus={openDropdownIfThereAreOptions}
                    onClick={openDropdownIfThereAreOptions}
                    onKeyDown={(event): void => {
                        if (event.key === "Escape") {
                            inputRef?.current?.blur();
                        }
                        if (event.key === "Enter") {
                            event.stopPropagation();
                            onOptionManuallyTyped(inputCurrentValue);
                        }
                    }}
                />
                <DropdownContent visibleItemsCount={10}>
                    <DropdownSelect
                        currentIndex={currentIndex}
                        role="listbox"
                        labelledById={`field-${testId}`}
                    >
                        {options.map((option, i) => (
                            <DropdownSelectItem
                                data-month-label={option.item.label}
                                key={`${option.item.id}-${i}`}
                                role="option"
                                floated={option.floated}
                                onClick={(): void =>
                                    onOptionSelectedFromDropdown(option.item)
                                }
                                label={option.item.label}
                                floatedItemMinWidth={35}
                                {...option.icons}
                            />
                        ))}
                    </DropdownSelect>
                </DropdownContent>
            </Dropdown>
        </Box>
    );
};

export default MonthsBasedDatePicker;
